/**
 * @file src/components/atoms/Tabs/Tabs.tsx
 * @description Defines a customizable Tabs component that allows users to navigate between different content panels.
 * The component includes an animated tab indicator, supports customizable tab headers, and handles dynamic resizing.
 * It offers various configurations for color, spacing, sizing, and roundedness. The component integrates with
 * Framer Motion for smooth animations and updates on window resizing.
 * @module Tabs
 */

import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { motion } from 'framer-motion';
import classnames from 'classnames';
import APP_CONFIG from '@/config/app';
import { Text } from '@/components';
import { TextVariants } from '@/interfaces';

const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';

const {
  MAIN_GAP,
  MAIN_ROUNDEDNESS,
  TEXT: { SMALL_HEADING, SMALL_HEADING_COLOR },
} = APP_CONFIG.STYLES;

const spacingConfig = {
  gap: `gap${MAIN_GAP}`,
  headersListPadding: `p${MAIN_GAP} px-0`,
  headersListGap: 'gap-0',
  headerPadding: `px${MAIN_GAP}`,
  selectedHeaderPadding: 'top-0',
};

const sizingConfig = {
  width: 'w-full',
  height: 'h-full',
  headerHeight: 'h-full',
  selectedHeaderHeight: 'h-full',
  bodyWidth: 'w-full',
  bodyHeight: 'h-full',
};

const colorConfig = {
  headersListBackground: 'bg-primary/5',
  headersListBorder: 'border border-primary/5',
  selectedTabBackground: 'bg-primary/10',
  selectedTabBorder: 'border-none',
  unselectedTextColor: 'text-secondary',
  selectedTextColor: 'text-primary',
};

const roundednessConfig = {
  headersListRoundedness: `rounded${MAIN_ROUNDEDNESS}`,
  itemRoundedness: 'rounded-none',
};

const textConfig = {
  tabTextSize: SMALL_HEADING,
  selectedTabTextColor: SMALL_HEADING_COLOR,
  unselectedTabTextColor: 'secondary' as TextVariants['color'],
};

interface TabProps {
  label: string;
  children: ReactNode;
}

interface TabsProps {
  tabs: TabProps[];
  onTabChange?: (index: number) => void;
  activeTabIndex?: number;
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  onTabChange,
  activeTabIndex = 0,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(activeTabIndex);
  const [tabIndicatorWidth, setTabIndicatorWidth] = useState(0);
  const [tabIndicatorX, setTabIndicatorX] = useState(0);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleTabClick = useCallback(
    (index: number) => {
      setSelectedIndex(index);
      if (onTabChange) {
        onTabChange(index);
      }
    },
    [onTabChange],
  );

  // Function to update the position and width of the indicator
  const updateIndicator = useCallback(() => {
    if (tabRefs.current[selectedIndex]) {
      const tabElement = tabRefs.current[selectedIndex]!;
      const parentElement = tabElement.parentElement!;
      const { width, left } = tabElement.getBoundingClientRect();
      const parentRect = parentElement.getBoundingClientRect();
      const parentLeft = parentRect.left;
      const parentWidth = parentRect.width;

      // Calculate the tab's position relative to the parent element
      const tabLeft = left - parentLeft;

      // Adjust for the first tab
      if (selectedIndex === 0) {
        setTabIndicatorX(0);
      } else {
        setTabIndicatorX(tabLeft);
      }

      // Adjust for the last tab
      if (selectedIndex === tabs.length - 1) {
        setTabIndicatorWidth(parentWidth - tabLeft);
      } else {
        setTabIndicatorWidth(width);
      }
    }
  }, [selectedIndex, tabs.length]);

  useEffect(() => {
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => {
      window.removeEventListener('resize', updateIndicator);
    };
  }, [selectedIndex, updateIndicator]);

  useEffect(() => {
    if (activeTabIndex !== selectedIndex) {
      setSelectedIndex(activeTabIndex);
    }
  }, [activeTabIndex, selectedIndex]);

  // Retrieve animation settings from the config
  const animationConfig = APP_CONFIG.ANIMATION_SETTINGS.COMMON;

  return (
    <div
      className={`flex flex-col flex-grow overflow-hidden 
        ${sizingConfig.width} 
        ${sizingConfig.height} 
        ${spacingConfig.gap} 
        ${borderClassToVisualizeElementsAndSpacing}`}
    >
      {tabs.length > 1 && (
        <div
          className={`flex relative overflow-hidden 
            ${spacingConfig.headersListPadding} 
            ${spacingConfig.headersListGap} 
            ${roundednessConfig.headersListRoundedness} 
            ${colorConfig.headersListBackground} 
            ${colorConfig.headersListBorder} 
            ${borderClassToVisualizeElementsAndSpacing}`}
          style={{ boxSizing: 'border-box' }}
        >
          <motion.div
            className={`absolute 
              ${spacingConfig.selectedHeaderPadding} 
              ${sizingConfig.selectedHeaderHeight} 
              ${colorConfig.selectedTabBackground} 
              ${colorConfig.selectedTabBorder} 
              ${roundednessConfig.itemRoundedness} 
              ${borderClassToVisualizeElementsAndSpacing}`}
            style={{ width: `${tabIndicatorWidth}px` }}
            animate={{ x: tabIndicatorX }}
            transition={animationConfig}
          />
          {tabs.map((tab, index) => (
            <div
              key={index}
              ref={(el) => (tabRefs.current[index] = el)}
              className={classnames(
                `relative flex-1 cursor-pointer flex items-center justify-center 
                ${sizingConfig.headerHeight} 
                ${roundednessConfig.itemRoundedness} 
                ${spacingConfig.headerPadding} 
                ${borderClassToVisualizeElementsAndSpacing}`,
                {
                  [colorConfig.selectedTextColor]: index === selectedIndex,
                  [colorConfig.unselectedTextColor]: index !== selectedIndex,
                },
              )}
              data-selected={index === selectedIndex}
              onClick={() => handleTabClick(index)}
            >
              <Text
                value={tab.label}
                size={textConfig.tabTextSize}
                color={
                  index === selectedIndex
                    ? textConfig.selectedTabTextColor
                    : textConfig.unselectedTabTextColor
                }
              />
            </div>
          ))}
        </div>
      )}
      <div
        className={`flex flex-col flex-grow items-start 
          ${sizingConfig.bodyWidth} 
          ${sizingConfig.bodyHeight} 
          ${borderClassToVisualizeElementsAndSpacing}`}
      >
        {tabs.length > 0 && tabs[selectedIndex] && tabs[selectedIndex].children}
      </div>
    </div>
  );
};

Tabs.defaultProps = {
  tabs: [],
  onTabChange: undefined,
  activeTabIndex: 0,
};

export default Tabs;
